import { useState, useCallback, FunctionComponent, useEffect } from 'react';

// libs
import * as Yup from 'yup';
import { Formik } from 'formik';
import { phoneRegExp, useSupportAirTable, CallbackDataType } from '@Libs/utils';

// Contexts
import { useAccountContext } from '@Components/Account/Contexts';

// Utilities
import classnames from 'classnames';

// Components
import Form from '../Components/Form';
import Thanks from '../Components/Thanks';
import Image from '@Components/Utils/Image';
import { useViewDetected } from '@Components/Utils/ViewDetected';

// Styles
import styles from './FormCooperation.module.scss';

const OPTIONS = {
    doctor: {
        title: 'Đăng ký hợp tác Bác sĩ',
        icon: '/assets/bottomAction/doctor.png',
        subtitle: 'Hơn <b>800</b> Bác sĩ đã hợp tác<br />với eDoctor đến từ các Bệnh viện<br />lớn và uy tín trên cả nước.',
    },
    clinic: {
        icon: '/assets/bottomAction/clinic.png',
        title: 'Đăng ký hợp tác<br/>phòng khám, cơ sở y tế',
        subtitle: 'Hơn <b>100</b> Phòng khám<br/>hợp tác cùng eDoctor',
    },
    nursing: {
        title: 'Đăng ký hợp tác Điều dưỡng',
        icon: '/assets/bottomAction/nursing.png',
        subtitle: 'Hơn <b>600</b> Điều dưỡng năng động<br />và đầy nhiệt huyết đã hợp tác với eDoctor ',
    },
    enterprise: {
        icon: '/assets/bottomAction/enterprise.png',
        title: 'Tư vấn khám sức khỏe<br/>dành cho nhân viên',
        subtitle: 'Hơn <b>100,000</b> lượt khám, được nhiều doanh nghiệp tin dùng.',
    },
};

interface FormCooperationProps {
    tab: string;
    className?: string;
    onBack?: () => void;
    onBackRoot?: () => void;
}

interface FieldItem {
    key: string;
    name: string;
}

const AIRTABLE_FIELDS: Array<FieldItem> = [
    { name: 'Name', key: 'name' },
    { name: 'Email', key: 'email' },
    { name: 'Số điện thoại', key: 'phoneNumber' },
];

const AIRTABLE_OPTION_FIELDS: { [key: string]: Array<FieldItem> } = {
    doctor: [{ name: 'Chuyên khoa của Bác sĩ', key: 'specialist' }],
    clinic: [
        { name: 'Thông tin PK', key: 'clinicName' },
        { name: 'Địa chỉ', key: 'clinicAddress' },
    ],
    nursing: [{ name: 'Khu vực công tác', key: 'address' }],
    enterprise: [
        { name: 'Thông tin doanh nghiệp', key: 'enterpriseName' },
        { name: 'Số lượng nhân viên', key: 'enterpriseMember' },
    ],
};

const BASE_ID = 'apprV0ryUoLMqsih1';

const BASE_FORM_OPTION: { [key: string]: string } = {
    doctor: 'Form Hợp tác Bác sĩ',
    clinic: 'Form hợp tác PK',
    nursing: 'Form hợp tác CTV',
    enterprise: 'Form Khám DN',
};

const FormCooperation: FunctionComponent<FormCooperationProps> = ({ tab, className, onBack, onBackRoot }) => {
    const { summary } = useAccountContext();
    const { deviceType } = useViewDetected();

    const { loading, sendAirTable } = useSupportAirTable(BASE_ID);

    const [isThanks, setIsThanks] = useState<boolean>(false);

    const initValue = {
        name: '',
        email: '',
        address: '',
        clinicName: '',
        specialist: '',
        clinicAddress: '',
        enterpriseName: '',
        enterpriseMember: '',
        phoneNumber: summary?.consumer?.phoneNumber?.replace('+84', '0') || '',
    };

    const validationSchema = Yup.object({
        name: Yup.string().required('Vui lòng nhập tên!'),
        email: Yup.string().email('Email chưa đúng định dạng!'),
        phoneNumber: Yup.string().required('Vui lòng nhập số điện thoại!').min(9, 'Số điện thoại chưa đúng!').matches(phoneRegExp, 'Số điện thoại chưa đúng!'),
        ...{
            doctor: {
                specialist: Yup.string().required('Vui lòng nhập chuyên khoa!'),
            },
            clinic: {
                clinicName: Yup.string().required('Vui lòng nhập tên phòng khám!'),
                clinicAddress: Yup.string().required('Vui lòng nhập địa chỉ phòng khám!'),
            },
            nursing: {
                address: Yup.string().required('Vui lòng nhập nơi làm việc!'),
            },
            enterprise: {
                enterpriseName: Yup.string().required('Vui lòng nhập tên doanh nghiệp!'),
                enterpriseMember: Yup.number().typeError('Số lượng nhân viên chưa đúng!').required('Vui lòng nhập số lượng nhân viên!'),
            },
        }[tab],
    });

    const headContent: { title: string; icon: string; subtitle: string } = OPTIONS[tab] || {};

    const goBack = useCallback(() => onBack(), []);

    useEffect(() => {
        if (!tab) {
            goBack();
            setIsThanks(false);
        }
    }, [tab]);

    const backRoot = useCallback(() => {
        onBackRoot();
    }, []);

    const callback = (data: CallbackDataType) => {
        if (data.type === 'success') {
            setIsThanks(true);
        }
    };

    const handleSubmit = (values) => {
        const fields = [...AIRTABLE_FIELDS, ...AIRTABLE_OPTION_FIELDS[tab]].reduce((fieldObj, fieldItem: FieldItem) => {
            fieldObj[fieldItem.name] = values[fieldItem.key];
            return fieldObj;
        }, {});

        sendAirTable(BASE_FORM_OPTION[tab], fields, '', callback);
    };

    const wrapperClassName = classnames([
        styles.wrapper,
        styles[deviceType],
        {
            [styles.active]: isThanks,
        },
        className,
    ]);

    return (
        <div className={wrapperClassName}>
            <div className={styles.form_wrapper}>
                <div className={styles.head_content}>
                    <span className={styles.back} onClick={goBack} />
                    <div className={styles.content}>
                        <div
                            className={styles.title}
                            dangerouslySetInnerHTML={{
                                __html: headContent.title,
                            }}
                        />

                        <div
                            className={styles.subtitle}
                            dangerouslySetInnerHTML={{
                                __html: headContent.subtitle,
                            }}
                        />

                        <Image src={headContent.icon} width={64} height={64} className={styles.icon} />
                    </div>
                </div>

                <Formik initialValues={initValue} onSubmit={handleSubmit} validationSchema={validationSchema}>
                    <Form tab={tab} loading={loading} />
                </Formik>
            </div>
            <div className={styles.thank_you}>
                <span className={styles.back} onClick={backRoot} />
                <Thanks className={styles.thanks} />
            </div>
        </div>
    );
};

FormCooperation.defaultProps = {
    onBack: () => null,
    onBackRoot: () => null,
};

export default FormCooperation;
